// react and js
import { React, useState, useRef } from "react";
import PropTypes from "prop-types";

// mui
import { Box, IconButton, DialogTitle, DialogContent, DialogActions, Typography } from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import Iconify from "../../../components/Iconify";

// theme
import { B1Tiny, ModalActionsLoadingButton } from "../../../theme/styled";

// utils
import { ModuleDataToTxtData } from "../../../utils/moduleConversion";

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function HandlePreviewFileContent({ moduleData }) {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  HandlePreviewFileContent.propTypes = {
    moduleData: PropTypes.object.isRequired,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [open, setOpen] = useState(false);
  const [txtData, setTxtData] = useState("");
  const contentRef = useRef(null);
  const [scroll, setScroll] = useState("paper");

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => setOpen(false);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(txtData);
    } catch (err) {
      console.error("Failed to copy content: ", err);
      alert("Failed to copy content to clipboard");
    }
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClickOpen = (scrollType) => () => {
    // Convert module data to text only when opening the dialog
    const convertedTxtData = ModuleDataToTxtData(moduleData);
    setTxtData(convertedTxtData);
    setOpen(true);
    setScroll(scrollType);
  };

  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        File preview
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        maxWidth="lg"
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">File preview - {moduleData.moduleName}</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <DialogContent
              dividers
              sx={{
                background: "#F2F2F2",
                borderTop: "1px solid #C8C8C8",
                borderBottom: "0px solid #C8C8C8",
                px: 2,
                pt: 2,
                pb: 1,
                width: "100%",
                boxSizing: "border-box",
                overflowY: "scroll",
                overflowX: "hidden",
                // Custom scrollbar styling
                "&::-webkit-scrollbar": {
                  width: "16px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#e0e0e0",
                  borderRadius: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888888",
                  borderRadius: "6px",
                  border: "2px solid #e0e0e0",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#666666",
                },
                // Firefox scrollbar styling
                scrollbarWidth: "thin",
                scrollbarColor: "#888888 #e0e0e0",
              }}
            >
              <Box
                ref={contentRef}
                sx={{
                  fontFamily: 'Monaco, "Lucida Console", "Courier New", monospace',
                  fontSize: "12px",
                  lineHeight: 1.4,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  backgroundColor: "white",
                  padding: 2,
                  borderRadius: 1,
                  border: "1px solid #C8C8C8",
                  overflow: "auto",
                }}
              >
                {txtData}
              </Box>
            </DialogContent>

            <DialogActions
              sx={{
                background: "#F2F2F2",
                p: 2,
                pt: 1,
                display: "flex",
                alignItems: "flex-start",
                flexDirection: "column",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                <ModalActionsLoadingButton fullWidth size="large" onClick={handleCopyToClipboard}>
                  Copy to clipboard
                </ModalActionsLoadingButton>

                <ModalActionsLoadingButton fullWidth size="large" variant="contained" onClick={handleClose}>
                  Close
                </ModalActionsLoadingButton>
              </Box>
            </DialogActions>
          </>
        }
      />
    </>
  );
}